import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Box, Typography, IconButton, InputBase, Paper, Tabs, Tab, CircularProgress } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import SendIcon from '@mui/icons-material/Send';
import { sendMessage } from '../../services/togetherAiService';
import ReactMarkdown from 'react-markdown';

const ChatContainer = styled(Box)`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 70%;
  background-color: white;
  border-top-left-radius: 24px;
  border-top-right-radius: 24px;
  display: flex;
  flex-direction: column;
  z-index: 1000;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  border: 2px solid #FF69B4;
  border-bottom: none;
`;

const ChatHeader = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  position: relative;
  margin-top: 26px;
`;

const EyeIcon = styled.div`
  width: 88px;
  height: 53px;
  border-radius: 50%;
  background-color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: -26px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1002;
`;


const BackButton = styled(IconButton)`
  color: #666;
`;

const TabsContainer = styled(Box)`
  border-bottom: 1px solid #eee;
  
  .MuiTabs-indicator {
    background-color: #FF69B4;
  }
`;

const StyledTab = styled(Tab)`
  text-transform: none;
  font-weight: 500;
  
  &.Mui-selected {
    color: #FF69B4;
  }
`;

const ChatContent = styled(Box)`
  flex: 1;
  padding: 16px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SuggestionTitle = styled(Typography)`
  color: #FF69B4;
  font-size: 14px;
  margin-bottom: 8px;
  font-weight: 500;
`;

const SuggestionItem = styled(Typography)`
  color: #333;
  padding: 10px 0;
  border-bottom: 1px solid #f0f0f0;
  font-size: 15px;
  cursor: pointer;
  
  &:hover {
    color: #FF69B4;
  }
`;

const InputContainer = styled(Paper)`
  display: flex;
  align-items: center;
  padding: 8px 16px;
  margin: 16px;
  border-radius: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #FF69B4;
`;

const StyledInput = styled(InputBase)`
  flex: 1;
  margin-left: 8px;
`;

const SendButton = styled(IconButton)`
  color: #FF69B4;
  &:disabled {
    color: #ccc;
  }
`;

const MessageContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
`;

const UserMessage = styled(Box)`
  align-self: flex-end;
  background-color: #FF69B4;
  color: white;
  padding: 12px 16px;
  border-radius: 18px 18px 0 18px;
  max-width: 80%;
  word-wrap: break-word;
`;

const AIMessage = styled(Box)`
  align-self: flex-start;
  background-color: #f0f0f0;
  color: #333;
  padding: 12px 16px;
  border-radius: 18px 18px 18px 0;
  max-width: 80%;
  word-wrap: break-word;
  
  /* Markdown styling */
  p {
    margin: 0 0 10px 0;
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  h1, h2, h3, h4, h5, h6 {
    margin-top: 10px;
    margin-bottom: 10px;
    font-weight: 600;
  }
  
  code {
    background-color: rgba(0, 0, 0, 0.05);
    padding: 2px 4px;
    border-radius: 4px;
    font-family: monospace;
  }
  
  pre {
    background-color: rgba(0, 0, 0, 0.05);
    padding: 10px;
    border-radius: 4px;
    overflow-x: auto;
    margin: 10px 0;
    
    code {
      background-color: transparent;
      padding: 0;
    }
  }
  
  ul, ol {
    margin: 10px 0;
    padding-left: 20px;
  }
  
  blockquote {
    border-left: 3px solid #ccc;
    margin: 10px 0;
    padding-left: 10px;
    color: #666;
  }
  
  a {
    color: #FF69B4;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
  
  img {
    max-width: 100%;
    border-radius: 4px;
    margin: 10px 0;
  }
  
  table {
    border-collapse: collapse;
    width: 100%;
    margin: 10px 0;
    
    th, td {
      border: 1px solid #ddd;
      padding: 8px;
      text-align: left;
    }
    
    th {
      background-color: rgba(0, 0, 0, 0.05);
    }
    
    tr:nth-child(even) {
      background-color: rgba(0, 0, 0, 0.02);
    }
  }
`;

const ChatInterface = ({ onClose }) => {
  const [tabValue, setTabValue] = useState(0);
  const [inputValue, setInputValue] = useState('');
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const chatContentRef = useRef(null);
  
  const connectionTopics = [
    'Overcoming social anxiety',
    'Preparation for community events',
    'Ways to discuss your goals',
    'Build process for prizing a music deal'
  ];
  
  const expressionTopics = [
    'Finding your authentic sound',
    'Techniques for vocal performance',
    'Songwriting inspiration methods',
    'Developing your stage presence',
    'Storytelling through lyrics'
  ];
  
  const awarenessTopics = [
    'Understanding music industry trends',
    'Recognizing opportunities for growth',
    'Analyzing audience engagement',
    'Identifying your unique value proposition',
    'Mindfulness practices for creativity'
  ];
  
  // Get the current topics based on selected tab
  const getCurrentTopics = () => {
    switch(tabValue) {
      case 0:
        return connectionTopics;
      case 1:
        return expressionTopics;
      case 2:
        return awarenessTopics;
      default:
        return connectionTopics;
    }
  };

  // Scroll to bottom of chat when messages change
  useEffect(() => {
    if (chatContentRef.current) {
      chatContentRef.current.scrollTop = chatContentRef.current.scrollHeight;
    }
  }, [messages]);
  
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };
  
  const handleSuggestionClick = (suggestion) => {
    // Set the input value to the suggestion
    setInputValue(suggestion);
    
    // Add user message to chat
    setMessages(prev => [...prev, { role: 'user', content: suggestion }]);
    
    // Clear input field after sending
    setInputValue('');
    
    // Set loading state
    setIsLoading(true);
    
    // Send message to AI
    (async () => {
      try {
        // Prepare conversation history for API
        const conversationHistory = [
          { role: 'system', content: 'You are JJ Muse, an AI assistant designed to help musicians navigate the music industry. You provide helpful, concise, and practical advice.' },
          ...messages,
          { role: 'user', content: suggestion }
        ];
        
        // Send message to Together.ai API
        const response = await sendMessage(conversationHistory);
        
        // Add AI response to chat
        setMessages(prev => [...prev, { role: 'assistant', content: response.content }]);
      } catch (error) {
        console.error('Error sending message:', error);
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: 'Sorry, I encountered an error. Please check your API key in the .env file or try again later.'
        }]);
      } finally {
        setIsLoading(false);
      }
    })();
  };
  
  const handleSend = async () => {
    if (inputValue.trim()) {
      const userMessage = inputValue.trim();
      setInputValue('');
      
      // Add user message to chat
      setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
      
      setIsLoading(true);
      
      try {
        // Prepare conversation history for API
        const conversationHistory = [
          { role: 'system', content: 'You are JJ Muse, an AI assistant designed to help musicians navigate the music industry. You provide helpful, concise, and practical advice.' },
          ...messages,
          { role: 'user', content: userMessage }
        ];
        
        // Send message to Together.ai API
        const response = await sendMessage(conversationHistory);
        
        // Add AI response to chat
        setMessages(prev => [...prev, { role: 'assistant', content: response.content }]);
      } catch (error) {
        console.error('Error sending message:', error);
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: 'Sorry, I encountered an error. Please check your API key in the .env file or try again later.'
        }]);
      } finally {
        setIsLoading(false);
      }
    }
  };
  
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };
  
  const renderMessages = () => {
    if (messages.length === 0) {
      return (
        <>
          <SuggestionTitle>Common Topics</SuggestionTitle>
          {getCurrentTopics().map((suggestion, index) => (
            <SuggestionItem 
              key={index} 
              onClick={() => handleSuggestionClick(suggestion)}
            >
              {suggestion}
            </SuggestionItem>
          ))}
        </>
      );
    }
    
    return (
      <MessageContainer>
        {messages.map((message, index) => {
          if (message.role === 'user') {
            return (
              <UserMessage key={index}>
                {message.content}
              </UserMessage>
            );
          } else if (message.role === 'assistant') {
            return (
              <AIMessage key={index}>
                <ReactMarkdown>
                  {message.content}
                </ReactMarkdown>
              </AIMessage>
            );
          }
          return null;
        })}
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', padding: 2 }}>
            <CircularProgress size={24} sx={{ color: '#FF69B4' }} />
          </Box>
        )}
      </MessageContainer>
    );
  };
  
  return (
    <>
      <ChatContainer>
        <ChatHeader>
          <BackButton onClick={onClose}>
            <ArrowBackIosNewIcon fontSize="small" />
          </BackButton>
          <EyeIcon>
            <img src="/eye.png" alt="Eye" width="88" height="53" />
          </EyeIcon>
        </ChatHeader>
        
        <TabsContainer>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            variant="fullWidth"
            centered
          >
            <StyledTab label="Connection" style={{ backgroundColor: tabValue === 0 ? '#E6A9D3' : 'transparent', borderRadius: '20px 20px 0 0' }} />
            <StyledTab label="Expression" style={{ backgroundColor: tabValue === 1 ? '#E6A9D3' : 'transparent', borderRadius: '20px 20px 0 0' }} />
            <StyledTab label="Awareness" style={{ backgroundColor: tabValue === 2 ? '#E6A9D3' : 'transparent', borderRadius: '20px 20px 0 0' }} />
          </Tabs>
        </TabsContainer>
        
        <ChatContent ref={chatContentRef}>
          {renderMessages()}
        </ChatContent>
        
        <InputContainer>
          <StyledInput
            placeholder="Ask Muse anything"
            value={inputValue}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            multiline
            maxRows={3}
            fullWidth
          />
          <SendButton 
            onClick={handleSend}
            disabled={!inputValue.trim() || isLoading}
          >
            <SendIcon />
          </SendButton>
        </InputContainer>
      </ChatContainer>
    </>
  );
};

export default ChatInterface;
